import { useEffect, useState, useContext } from "react";
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetActions, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePortalPages, useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import ActionTable from "../ActionTable/ActionTable";
import AttributeTable from "../AttributeTable/AttributeTable";
import WorkWeekMultiSelect from "./CalendarComponents/WorkWeekMultiSelect";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import DependentProperty from "./GanttChartComponents/DependentProperty";
import MappingAttributesForGanntChart from "./GanttChartComponents/MappingAttributesForGanntChart";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import WorkProperty from "./GanttChartComponents/WorkProperty";
import DurationProperty from "./GanttChartComponents/DurationProperty";
import ProgressProperty from "./GanttChartComponents/ProgressProperty";
import EventActions from "./CalendarComponents/EventActions";
import { getAttributes } from "../../../API/Attribute/Attribute";
import ResourceProperty from "./GanttChartComponents/ResourceProperty";

function GanttChart() {

    const [gridLinesData, setGridLinesData] = useState([
        {
            "value": "Both",
            "text": "Both"
        },
        {
            "value": "Horizontal",
            "text": "Horizontal"
        },
        {
            "value": "Vertical",
            "text": "Vertical"
        },
        {
            "value": "None",
            "text": "None"
        }
    ]);

    const [ganttChartViewData, setGanttChartViewData] = useState([
        {
            "value": "Task View",
            "text": "Task View"
        },
        {
            "value": "Resource View",
            "text": "Resource View"
        }
    ]);

    const workWeekCountData = ["1", "2", "3", "4", "5"];

    const [unitData, setUnitData] = useState([
        {
            "text": "Year",
            "value": "Year"
        },
        {
            "text": "Month",
            "value": "Month"
        },
        {
            "text": "Week",
            "value": "Week"
        },
        {
            "text": "Day",
            "value": "Day"
        },
        {
            "text": "Hour",
            "value": "Hour"
        }
    ]);

    const [sortOrderData, setSortOrderData] = useState([
        {
            "text": "Ascending",
            "value": "Ascending"
        },
        {
            "text": "Descending",
            "value": "Descending"
        }
    ]);

    const formData = {
        "Year": [
            "Jan '18",
            "2018",
            "January 18"
        ],
        "Month": [
            "Jan 01, 2018",
            "January",
            "Jan"
        ],
        "Week": [
            "Jan 01, 2018",
            "Mon Jan 01, '18",
            "Mon Jan 01"
        ],
        "Day": [
            "Mon, 01",
            "Mon",
            "01"
        ],
        "Hour": [
            "00",
            "00 : 00 AM",
            "0 : 00 AM"
        ]
    }

    const [topFormatData, setTopFormatData] = useState(formData["Week"]);
    const [bottomFormatData, setBottomFormatData] = useState(formData["Day"]);

    const formProperties = componentPropertiesMap["Gantt Chart"];
    const { user } = useContext(GlobalContext).state;
    // hooks
    const selectedPage = useSelectedPage();
    const portalPages = usePortalPages();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");
    const advancedMode = usePropsStoreValue("advancedMode");
    const isInternalUser = useInternalUserCheck(user.email);
    const saveComponent = useSaveComponent();
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetContext = useResetComponentProperties();
    const resetActions = useResetActions();
    const resetAttributes = useResetAttributeTable();

    const [properties, setProperties] = useComponentProperties();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();
    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const [loading, setLoading] = useState(true);

    const { t, ready } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/GanttChart",
        "display/tables/attribute",
        "display/components/calendar",
    ])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            getData();
        }
    }, [])

    useEffect(() => {
        if (!loading) {
            setComponentPropertiesUsingAttributes(fetchedValues.attributes)
        }
    }, [selectedPage, selectedComponent])

    useEffect(() => {
        if (!loading) {
            setIsChanged();
        }
    }, [properties])

    useEffect(() => {
        if (ready) {
            setGridLinesData([
                {
                    "text": t("grid_lines.both", { ns: "display/components/GanttChart" }),
                    "value": "Both"
                },
                {
                    "text": t("grid_lines.horizontal", { ns: "display/components/GanttChart" }),
                    "value": "Horizontal"
                },
                {
                    "text": t("grid_lines.vertical", { ns: "display/components/GanttChart" }),
                    "value": "Vertical"
                },
                {
                    "text": t("grid_lines.none", { ns: "display/components/GanttChart" }),
                    "value": "None"
                }
            ]);
            setGanttChartViewData([
                {
                    "text": t("gantt_chart_views.task_view", { ns: "display/components/GanttChart" }),
                    "value": "Task View"
                },
                {
                    "text": t("gantt_chart_views.resource_view", { ns: "display/components/GanttChart" }),
                    "value": "Resource View"
                }
            ]);
            setUnitData(
                [
                    {
                        "text": t("units.year", { ns: "display/components/GanttChart" }),
                        "value": "Year"
                    },
                    {
                        "text": t("units.month", { ns: "display/components/GanttChart" }),
                        "value": "Month"
                    },
                    {
                        "text": t("units.week", { ns: "display/components/GanttChart" }),
                        "value": "Week"
                    },
                    {
                        "text": t("units.day", { ns: "display/components/GanttChart" }),
                        "value": "Day"
                    },
                    {
                        "text": t("units.hour", { ns: "display/components/GanttChart" }),
                        "value": "Hour"
                    }
                ]
            );
            setSortOrderData([
                {
                    "text": t("sort_orders.ascending", { ns: "display/components/GanttChart" }),
                    "value": "Ascending"
                },
                {
                    "text": t("sort_orders.descending", { ns: "display/components/GanttChart" }),
                    "value": "Descending"
                }
            ]);
        }
    }, [ready])

    // state
    const [tabSelected, setSelectedTab] = useState(0);

    // functions

    async function getData() {
        const attributes = await getAttributes(entityId);
        const attributesToStore = attributes.sort((a, b) => {
            if (a.isSystemAttribute && !b.isSystemAttribute) {
                return 1
            } else if (a.isSystemAttribute && b.isSystemAttribute) {
                return 0;
            } else {
                return -1
            }
        })
        setComponentPropertiesUsingAttributes(attributesToStore);
        updateGeneralFetchedValue("attributes", attributesToStore)
        setChangeProperty("isGeneralPropertiesChanged", false);
        setLoading(false);
    }

    function setComponentPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            switch (property) {
                case "startDateAttributeId":
                    const startDateAttribute = attributes.find(attribute => attribute._id === selectedComponent.startDateAttributeId);
                    setProperties("startDateAttribute", startDateAttribute || { id: "" });
                    break;
                case "endDateAttributeId":
                    const endDateAttribute = attributes.find(attribute => attribute._id === selectedComponent.endDateAttributeId);
                    setProperties("endDateAttribute", endDateAttribute || { id: "" });
                    break;

                case "dependentAttributeId":
                    const dependentAttribute = attributes.find(attribute => attribute._id === selectedComponent.dependentAttributeId);
                    setProperties("dependentAttribute", dependentAttribute || { id: "" });
                    break;
                case "workAttributeId":
                    const workAttribute = attributes.find(attribute => attribute._id === selectedComponent.workAttributeId);
                    setProperties("workAttribute", workAttribute || { id: "" });
                    break;
                case "resourceAttributeId":
                    const resourceAttribute = attributes.find(attribute => attribute._id === selectedComponent.resourceAttributeId);
                    setProperties("resourceAttribute", resourceAttribute || { id: "" });
                    break;
                case "durationAttributeId":
                    const durationAttribute = attributes.find(attribute => attribute._id === selectedComponent.durationAttributeId);
                    setProperties("durationAttribute", durationAttribute || { id: "" });
                    break;
                case "progressAttributeId":
                    const progressAttribute = attributes.find(attribute => attribute._id === selectedComponent.progressAttributeId);
                    setProperties("progressAttribute", progressAttribute || { id: "" });
                    break;
                case "addPageId": {
                    const page = portalPages.find(page => page._id === selectedComponent.addPageId);
                    setProperties("addPage", page);
                    break;
                }
                case "editPageId": {
                    const page = portalPages.find(page => page._id === selectedComponent.editPageId);
                    setProperties("editPage", page);
                    break;
                }
                case "sortByAttributeId":
                    const sortByAttribute = attributes.find(attribute => attribute._id === selectedComponent.sortByAttributeId);
                    setProperties("sortByAttribute", sortByAttribute || { id: "" });
                    break;
                case "gridLines":
                    const gridLines = gridLinesData.find(gridLine => gridLine.value === selectedComponent[property]);
                    const gridLinesValue = gridLines || gridLinesData[0];
                    gridLinesValue.text = t(`grid_lines.${gridLinesValue.value.toLowerCase()}`, { ns: "display/components/GanttChart" });
                    setProperties(property, gridLinesValue);
                    break;
                case "ganttChartView":
                    const ganttChartView = ganttChartViewData.find(view => view.value === selectedComponent[property]);
                    const ganttChartViewValue = ganttChartView || ganttChartViewData[0];
                    ganttChartViewValue.text = t(`gantt_chart_views.${ganttChartViewValue.value.toLowerCase().replace(" ", "_")}`, { ns: "display/components/GanttChart" });
                    setProperties(property, ganttChartViewValue);
                    break;
                case "topUnit":
                    const topUnit = unitData.find(unit => unit.value === selectedComponent[property]);
                    const topUnitValue = topUnit || unitData[0];
                    topUnitValue.text = t(`units.${topUnitValue.value.toLowerCase()}`, { ns: "display/components/GanttChart" });
                    setProperties(property, topUnitValue);
                    setTopFormatData(formData[selectedComponent[property]]);
                    break;
                case "bottomUnit":
                    const bottomUnit = unitData.find(unit => unit.value === selectedComponent[property]);
                    const bottomUnitValue = bottomUnit || unitData[0];
                    bottomUnitValue.text = t(`units.${bottomUnitValue.value.toLowerCase()}`, { ns: "display/components/GanttChart" });
                    setProperties(property, bottomUnitValue);
                    setBottomFormatData(formData[selectedComponent[property]]);
                    break;
                case "sortOrder":
                    const sortOrder = sortOrderData.find(sortOrder => sortOrder.value === selectedComponent[property]);
                    const sortOrderValue = sortOrder || sortOrderData[0];
                    sortOrderValue.text = t(`sort_orders.${sortOrderValue.value.toLowerCase()}`, { ns: "display/components/GanttChart" });
                    setProperties(property, sortOrder || sortOrderData[0]);
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined
                    ) {
                        setProperties(property, selectedComponent[property])
                    }
                    break;
            }
        }
    }

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function handleInputChange(event) {
        setProperties(event.target.name, event.target.value);
    }

    function handleTopUnitChange(event) {
        const unit = event.target.value.value;
        handleInputChange(event);
        setTopFormatData(formData[unit]);
        if (!formData[unit].includes(properties.topFormat.value)) {
            setProperties("topFormat", formData[unit][0] || "");
        }
    }

    function handleBottomUnitChange(event) {
        const unit = event.target.value.value;
        handleInputChange(event);
        setBottomFormatData(formData[unit]);
        if (!formData[unit].includes(properties.bottomFormat.value)) {
            setProperties("bottomFormat", formData[unit][0] || "");
        }
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "startDateAttributeId":
                    if (selectedComponent.startDateAttributeId !== properties.startDateAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "endDateAttributeId":
                    if (selectedComponent.endDateAttributeId !== properties.endDateAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "dependentAttributeId":
                    if (selectedComponent.dependentAttributeId !== properties.dependentAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "workAttributeId":
                    if (selectedComponent.workAttributeId !== properties.workAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "resourceAttributeId":
                    if (selectedComponent.resourceAttributeId !== properties.resourceAttribute._id) {
                        debugger
                        isChanged = true;
                    }
                    break;
                case "durationAttributeId":
                    if (selectedComponent.durationAttributeId !== properties.durationAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "progressAttributeId":
                    if (selectedComponent.progressAttributeId !== properties.progressAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "addPageId": {
                    if (
                        properties.addPage &&
                        selectedComponent.addPageId !== properties.addPage._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "editPageId": {
                    if (
                        properties.editPage &&
                        selectedComponent.editPageId !== properties.editPage._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "sortByAttributeId":
                    if (selectedComponent.sortByAttributeId !== properties.sortByAttribute._id) {
                        isChanged = true;
                    }
                    break;
                case "workWeek":
                    const previousWorkWeek = selectedComponent.workWeek
                    const currentWorkWeek = properties.workWeek
                    const addedItems = currentWorkWeek.filter(item => !previousWorkWeek.includes(item));
                    const removedItems = previousWorkWeek.filter(item => !currentWorkWeek.includes(item));
                    if (addedItems.length > 0 || removedItems.length > 0) {
                        isChanged = true;
                    }
                    break;
                case "gridLines":
                case "ganttChartView":
                case "topUnit":
                case "bottomUnit":
                case "sortOrder":
                    if (selectedComponent[property] !== properties[property].value) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setComponentPropertiesUsingAttributes(fetchedValues.attributes);
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    if (loading) {
        return (
            <ComponentFormSkeleton />
        )
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <div className="row">
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showDuration"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showDuration}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_duration", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showDependency"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showDependency}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_dependency", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showProgress"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showProgress}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_progress", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showBaseline"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showBaseline}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_baseline", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showCriticalPath"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showCriticalPath}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_critical_path", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="allowExport"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.allowExport}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.allow_export", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.grid_lines", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="gridLines"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleInputChange}
                                                    data={gridLinesData}
                                                    value={properties.gridLines}
                                                    textField="text"
                                                    dataItemKey="value"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.gantt_chart_view", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="ganttChartView"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleInputChange}
                                                    data={ganttChartViewData}
                                                    value={properties.ganttChartView}
                                                    textField="text"
                                                    dataItemKey="value"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="form-label">{t("form_labels.time_scale", { ns: "display/components/GanttChart" })}</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <Checkbox
                                                    name="topTimeScale"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.topTimeScale}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.top_time_scale", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="bottomTimeScale"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.bottomTimeScale}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.bottom_time_scale", { ns: "display/components/GanttChart" })}
                                                    className="me-2"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.top_week_count", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="topWeekCount"
                                                    disabled={selectedComponent.hidden || !properties.topTimeScale}
                                                    onChange={handleInputChange}
                                                    data={workWeekCountData}
                                                    value={properties.topWeekCount}
                                                />
                                            </div>
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.bottom_week_count", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="bottomWeekCount"
                                                    disabled={selectedComponent.hidden || !properties.bottomTimeScale}
                                                    onChange={handleInputChange}
                                                    data={workWeekCountData}
                                                    value={properties.bottomWeekCount}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.top_unit", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="topUnit"
                                                    disabled={selectedComponent.hidden || !properties.topTimeScale}
                                                    onChange={handleTopUnitChange}
                                                    data={unitData}
                                                    value={properties.topUnit}
                                                    textField="text"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.bottom_unit", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="bottomUnit"
                                                    disabled={selectedComponent.hidden || !properties.bottomTimeScale}
                                                    onChange={handleBottomUnitChange}
                                                    data={unitData}
                                                    value={properties.bottomUnit}
                                                    textField="text"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.top_format", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="topFormat"
                                                    disabled={selectedComponent.hidden || !properties.topTimeScale}
                                                    onChange={handleInputChange}
                                                    data={topFormatData}
                                                    value={properties.topFormat}
                                                />
                                            </div>
                                            <div className="col-3">
                                                <div className="form-label">{t("form_labels.bottom_format", { ns: "display/components/GanttChart" })}</div>
                                                <DropDownList
                                                    name="bottomFormat"
                                                    disabled={selectedComponent.hidden || !properties.bottomTimeScale}
                                                    onChange={handleInputChange}
                                                    data={bottomFormatData}
                                                    value={properties.bottomFormat}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <div className="form-label">
                                                    {t("form_labels.work_weeks", { ns: "display/components/GanttChart" })}
                                                </div>
                                                <WorkWeekMultiSelect disabled={selectedComponent.hidden} />
                                            </div>
                                        </div>
                                    </>
                                }
                                {/* <div className="row">
                                    <div className="col-3">
                                        <div className="form-label">{t("form_labels.sort_by", { ns: "display/components/GanttChart" })}</div>
                                        <DropDownList
                                            name="sortByAttribute"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            data={fetchedValues.attributes}
                                            textField="attributeName"
                                            value={properties.sortByAttribute}
                                        />
                                    </div>
                                    <div className="col-3">
                                        <div className="form-label">{t("form_labels.sort_order", { ns: "display/components/GanttChart" })}</div>
                                        <DropDownList
                                            name="sortOrder"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            data={sortOrderData}
                                            textField="text"
                                            value={properties.sortOrder}
                                        />
                                    </div>
                                </div> */}
                                <div className="d-block my-3">
                                    <div className="form-label">
                                        {t("form_labels.gantt_chart_mapping_properties", { ns: "display/components/GanttChart" })}
                                    </div>
                                    <MappingAttributesForGanntChart />
                                </div>
                                <div className="row">
                                    <div className="col-3">
                                        <div className="form-label">
                                            {t("form_labels.dependent_property", { ns: "display/components/GanttChart" })}
                                        </div>
                                        <DependentProperty />
                                    </div>
                                    <div className="col-3">
                                        <div className="form-label">
                                            {t("form_labels.work_property", { ns: "display/components/GanttChart" })}
                                        </div>
                                        <WorkProperty />
                                    </div>
                                    <div className="col-3">
                                        <div className="form-label">
                                            {t("form_labels.duration_property", { ns: "display/components/GanttChart" })}
                                        </div>
                                        <DurationProperty />
                                    </div>
                                    <div className="col-3">
                                        <div className="form-label">
                                            {t("form_labels.progress_property", { ns: "display/components/GanttChart" })}
                                        </div>
                                        <ProgressProperty />
                                    </div>
                                    <div className="col-3">
                                        <div className="form-label">
                                            {t("form_labels.resource_property", { ns: "display/components/GanttChart" })}
                                        </div>
                                        <ResourceProperty />
                                    </div>
                                </div>
                                <div className="row my-4">
                                    <div className="col-3">
                                        <h6>
                                            {t("form_labels.event_actions.label", { ns: "display/components/calendar" })}
                                        </h6>
                                        <EventActions />
                                    </div>
                                </div>
                                <div className="row my-4">
                                    <div className="form-label">
                                        {t("form_labels.task_bar_view_settings", { ns: "display/components/GanttChart" })}
                                    </div>
                                    <div className="col-3">
                                        <Checkbox
                                            name="showTaskLabel"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showTaskLabel}
                                            onChange={handleInputChange}
                                            label={t("form_labels.show_task_label", { ns: "display/components/GanttChart" })}
                                            className="me-2"
                                        />
                                    </div>
                                    <div className="col-3">
                                        <Checkbox
                                            name="showTaskProgress"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showTaskProgress}
                                            onChange={handleInputChange}
                                            label={t("form_labels.show_task_progress", { ns: "display/components/GanttChart" })}
                                            className="me-2"
                                        />
                                    </div>
                                    <div className="col-3">
                                        <Checkbox
                                            name="showResource"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showResource}
                                            onChange={handleInputChange}
                                            label={t("form_labels.show_resource", { ns: "display/components/GanttChart" })}
                                            className="me-2"
                                        />
                                    </div>
                                    <div className="col-3">
                                        <Checkbox
                                            name="showWork"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showWork}
                                            onChange={handleInputChange}
                                            label={t("form_labels.show_work", { ns: "display/components/GanttChart" })}
                                            className="me-2"
                                        />
                                    </div>
                                </div>
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    {
                        isInternalUser &&
                        <TabStripTab title={t("tabs.properties", { ns: "display/common" })}>
                            <AttributeTable />
                        </TabStripTab>
                    }
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div >
    )
}

export default GanttChart;